import React, { forwardRef, useEffect, useState, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import Dropzone from 'react-dropzone';
import Select from 'react-select';
import { Row, Col } from 'react-bootstrap';
import { customStyles } from '../helper';

// eslint-disable-next-line no-empty-pattern
const UploadDocuments = forwardRef(
  (
    {
      handleUpload,
      module,
      fromLibrary,
      onSelectedGradingGroupChange,
      showGradingGroupError,
      setShowGradingGroupError,
      fromGrading,
      documentTypeList,
      maxFileSizeMb,
      ..._props
    },
    ref,
  ) => {
    const { t } = useTranslation('common');
    const { t: tAssets } = useTranslation('assetLibraryAssets');

    const [error, setError] = useState({
      fileName: '',
      document: '',
      sizeError: '',
      typeError: '',
      documentType: '',
    });
    const [files, setFiles] = useState([]);
    const [gradingOptions, setGradingOptions] = useState([]);
    const [selectedGradingGroup, setSelectedGradingGroup] = useState([]);
    const reducer = useSelector((reducerState) => reducerState);
    const { BOMConfigDetails } = reducer;
    const { productDetails } = BOMConfigDetails;
    const [supportingDocuments, setSupportingDocuments] = useState({
      fileName: '',
      fileExtn: '',
      document: '',
      documentType: '',
    });

    const onFileDrop = (acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        const fileSize = (acceptedFiles[0].size / 1024 / 1024).toFixed(0);
        setError((err) => ({
          ...err,
          document: '',
          typeError: '',
        }));
        let maxFileSize = 20; // default max file size
        if (module === 'specificationDoc') {
          maxFileSize = 5;
        }
        const enforceSizeLimit = maxFileSizeMb !== null;
        if (enforceSizeLimit && fileSize > maxFileSize) {
          setError((err) => ({
            ...err,
            sizeError: t('UPLOAD_DOCUMENTS.SIZE_ERROR_MESSAGE'),
          }));
          setFiles([]);
          setSupportingDocuments((doc) => ({
            ...doc,
            document: '',
            fileExtn: '',
          }));
        } else {
          setError((err) => ({
            ...err,
            document: '',
            sizeError: '',
          }));
          const fileName = acceptedFiles[0].name.split('.').slice(0, -1).join('.');
          const fileExtn = acceptedFiles[0].name.substr(acceptedFiles[0].name.lastIndexOf('.') + 1);

          setSupportingDocuments((doc) => ({
            ...doc,
            document: acceptedFiles[0],
            fileName,
            fileExtn,
          }));
          setFiles(
            acceptedFiles.map((file) =>
              Object.assign(file, {
                preview: URL.createObjectURL(file),
              }),
            ),
          );
          if (module === 'specDoc') handleUpload();
        }
      } else {
        setError((state) => ({
          ...state,
          typeError: t('UPLOAD_DOCUMENTS.FILE_ERROR_MESSAGE'),
        }));
        if (fromLibrary && error.document) {
          setError((state) => ({
            ...state,
            document: '',
          }));
        }
      }
    };
    const handleSelectChange = (selectedList) => {
      if (selectedList?.length === 0) {
        setSelectedGradingGroup([]);
      } else {
        setShowGradingGroupError(false);
        setSelectedGradingGroup(selectedList);
      }
    };

    useEffect(() => {
      if (gradingOptions?.length === 1) {
        handleSelectChange(gradingOptions);
      } else {
        const values = gradingOptions?.filter((val) => !isEmpty(gradingOptions) && selectedGradingGroup?.length && selectedGradingGroup?.includes(val?.value));
        handleSelectChange(values);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gradingOptions]);

    useEffect(() => {
      let gradOptions = productDetails?.gradingGroups?.map((ele) => {
        ele._id = ele.value;
        return ele;
      });
      gradOptions = gradOptions?.length ? gradOptions : [];
      setGradingOptions(gradOptions);
    }, [productDetails]);

    useEffect(
      () => () => {
        files.forEach((file) => URL.revokeObjectURL(file.preview));
      },
      [files],
    );

    const filesName = files.map((file) => <span key={file.path}>{file.path}</span>);

    const validateForm = () => {
      let formIsValid = true;
      const errors = { fileName: '', document: '' };
      if (!supportingDocuments.fileName.replace(/\s/g, '')?.length) {
        formIsValid = false;
        errors.fileName = t('UPLOAD_DOCUMENTS.SELECTED_DOCUMENT_TITLE');
        errors.sizeError = '';
      }
      if (!supportingDocuments.document) {
        formIsValid = false;
        errors.document = t('UPLOAD_DOCUMENTS.SELECTED_DOCUMENT_FILE');
        errors.sizeError = '';
      }
      if (!supportingDocuments.documentType && module === 'specificationDoc') {
        formIsValid = false;
        errors.documentType = t('UPLOAD_DOCUMENTS.PLEASE_SELECT_DOCUMENT_TYPE');
        errors.sizeError = '';
      }
      setError((state) => ({
        ...state,
        ...errors,
      }));
      return formIsValid;
    };

    useEffect(() => {
      if (module === 'specificationDoc') {
        if (selectedGradingGroup?.length > 0) {
          onSelectedGradingGroupChange(selectedGradingGroup);
        } else {
          onSelectedGradingGroupChange([]);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGradingGroup]);

    useImperativeHandle(ref, () => ({
      selectedDocument() {
        // eslint-disable-next-line sonarjs/no-collapsible-if
        if (fromLibrary && !error.typeError) {
          if (validateForm()) {
            return supportingDocuments;
          }
        } else if (!fromLibrary && validateForm()) {
          return supportingDocuments;
        }

        if (module === 'specificationDoc') {
          if (validateForm()) {
            return supportingDocuments;
          }
        } else if (!fromLibrary && validateForm()) {
          return supportingDocuments;
        }
        // return supportingDocuments;
      },
    }));

    const handleChange = (event, fieldName) => {
      setSupportingDocuments({
        ...supportingDocuments,
        [fieldName]: fieldName === 'documentType' ? event : event?.currentTarget?.value,
      });
      setError((state) => ({
        ...state,
        [fieldName]: '',
      }));
    };
    const message = fromGrading ? t('UPLOAD_DOCUMENTS.DRAG_DROP_XLSX') : t('UPLOAD_DOCUMENTS.DRAG_AND_DROP_MESSAGE');
    return (
      <form noValidate>
        <div className="common-image-upload new-image-upload">
          <Dropzone accept={_props?.accept ? _props?.accept : ''} onDrop={onFileDrop}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <section className="upload-image-section common-upload-image p-4" {...getRootProps()}>
                <input {...getInputProps()} />
                <div className="text-center">
                  <p className="mb-0">
                    {isDragActive ? t("UPLOAD_DOCUMENTS.DROP_FILES_HERE") : message}{" "}
                    {_props?.accept && `${tAssets('assets.allowedFileExtension')}${_props?.accept}.`}
                  </p>
                  <p className="mb-0">{filesName}</p>
                </div>
                <label className="upload-icon ">
                  <input {...getInputProps()} type="file" className="img-Upload" />
                  <a href={() => false} className="mt-16 align-items-center justify-content-center">
                    <i className="icon icon-Upload-1 " />
                  </a>
                </label>
              </section>
            )}
          </Dropzone>
          {
            // show file error Massage
            Object.keys(error)?.length !== 0 && (
              <>
                {error?.typeError && (
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="error-message  text-center w-50">{error.typeError}</span>
                  </div>
                )}
                {error?.document && (
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="error-message  text-center w-50">{error.document}</span>
                  </div>
                )}
                {error?.sizeError && (
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="error-message  text-center w-50">{error.sizeError}</span>
                  </div>
                )}
              </>
            )
          }

          {!_props.hideName && (
            <div className="d-flex w-100 upload-text-data">
              <div className=" w-100">
                <input
                  type="text"
                  name="fileName"
                  value={supportingDocuments.fileName}
                  onChange={(e) => handleChange(e, 'fileName')}
                  className="input-doc-type form-control text-bordered"
                  placeholder={t('UPLOAD_DOCUMENTS.DOCUMENT_TITLE_PLACEHOLDER')}
                />
                <span className="error-message">{error.fileName}</span>
              </div>
            </div>
          )}

          {module === 'specificationDoc' && (
            <Row className="mt-16">
              <Col md={6}>
                <div className="w-100">
                  <input
                    type="text"
                    name="fileName"
                    value={supportingDocuments?.fileName}
                    onChange={(e) => handleChange(e, 'fileName')}
                    className="input-doc-type form-control text-bordered"
                    placeholder={t('SPEC_TEMP_LIBRARY.COL_DOC_TITLE')}
                  />
                  <span className="error-message">{error?.fileName}</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="w-100">
                  <Select
                    options={documentTypeList?.filter((item) => item?.isActive)}
                    getOptionLabel={(option) => `${option.name}`}
                    getOptionValue={(option) => `${option._id}`}
                    value={supportingDocuments?.documentType}
                    styles={customStyles}
                    className="common-select"
                    classNamePrefix="common-select"
                    onChange={(e) => handleChange(e, 'documentType')}
                    placeholder={t('SPEC_TEMP_LIBRARY.COL_DOC_TYPE')}
                  />
                  <span className="error-message">{error?.documentType}</span>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </form>
    );
  },
);

UploadDocuments.displayName = 'UploadDocuments';

export default UploadDocuments;
